import React, { useState, useEffect } from 'react';

// ============================================================
// CONFIGURATION — modifier ici pour personnaliser la maintenance
// ============================================================
const MAINTENANCE_CONFIG = {
  // Titre affiché sur la page
  title: "Site en maintenance",
  // Message principal
  message: "Nous préparons quelque chose de délicieux pour vous.",
  // Sous-message
  subMessage: "Du Sourire à la Fourchette revient très bientôt avec une nouvelle expérience.",
  // Date de retour estimée (null pour masquer le compte à rebours)
  // Format : new Date('2026-04-01T10:00:00')
  returnDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000), // Dans 2 jours par défaut
  // Email de contact (null pour masquer)
  contactEmail: "contact@dusourirealafourchette.fr",
  // Réseaux sociaux (laisser vide pour masquer)
  instagram: "https://instagram.com/dusourirealafourchette",
  facebook: "",
};
// ============================================================

const Maintenance = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [particles] = useState(() =>
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 6 + Math.random() * 6,
      size: 4 + Math.random() * 8,
      opacity: 0.15 + Math.random() * 0.35,
    }))
  );

  useEffect(() => {
    if (!MAINTENANCE_CONFIG.returnDate) return;
    const timer = setInterval(() => {
      const now = new Date();
      const diff = MAINTENANCE_CONFIG.returnDate - now;
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        clearInterval(timer);
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div style={styles.root}>
      {/* Fond animé */}
      <div style={styles.bgOverlay} />
      <div style={styles.bgGrain} />

      {/* Particules flottantes */}
      {particles.map((p) => (
        <div
          key={p.id}
          style={{
            ...styles.particle,
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            opacity: p.opacity,
            animation: `floatUp ${p.duration}s ${p.delay}s infinite ease-in-out`,
          }}
        />
      ))}

      {/* Contenu central */}
      <div style={styles.container}>

        {/* Logo / icône */}
        <div style={styles.logoWrap}>
          <div style={styles.logoIcon}>
            <svg viewBox="0 0 64 64" width="56" height="56" fill="none">
              {/* Fourchette stylisée */}
              <circle cx="32" cy="32" r="30" stroke="hsl(33,73%,51%)" strokeWidth="1.5" opacity="0.4"/>
              <path d="M22 14 L22 28 Q22 34 28 34 L28 50" stroke="hsl(33,73%,51%)" strokeWidth="2.5" strokeLinecap="round"/>
              <path d="M19 14 L19 22" stroke="hsl(33,73%,51%)" strokeWidth="2" strokeLinecap="round"/>
              <path d="M25 14 L25 22" stroke="hsl(33,73%,51%)" strokeWidth="2" strokeLinecap="round"/>
              {/* Couteau */}
              <path d="M42 14 Q46 20 42 28 L42 50" stroke="hsl(33,73%,51%)" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>

        {/* Titre principal */}
        <p style={styles.siteName}>Du Sourire à la Fourchette</p>
        <h1 style={styles.title}>{MAINTENANCE_CONFIG.title}</h1>

        {/* Séparateur décoratif */}
        <div style={styles.divider}>
          <span style={styles.dividerLine} />
          <span style={styles.dividerDot} />
          <span style={styles.dividerLine} />
        </div>

        {/* Message */}
        <p style={styles.message}>{MAINTENANCE_CONFIG.message}</p>
        <p style={styles.subMessage}>{MAINTENANCE_CONFIG.subMessage}</p>

        {/* Compte à rebours */}
        {MAINTENANCE_CONFIG.returnDate && (
          <div style={styles.countdown}>
            {[
              { label: 'Jours', value: timeLeft.days },
              { label: 'Heures', value: timeLeft.hours },
              { label: 'Minutes', value: timeLeft.minutes },
              { label: 'Secondes', value: timeLeft.seconds },
            ].map(({ label, value }, i) => (
              <div key={label} style={styles.countBlock}>
                <div style={styles.countNum}>
                  {String(value).padStart(2, '0')}
                </div>
                <div style={styles.countLabel}>{label}</div>
                {i < 3 && <div style={styles.countSep}>:</div>}
              </div>
            ))}
          </div>
        )}

        {/* Contact */}
        {MAINTENANCE_CONFIG.contactEmail && (
          <div style={styles.contactWrap}>
            <p style={styles.contactText}>Une question urgente ?</p>
            <a href={`mailto:${MAINTENANCE_CONFIG.contactEmail}`} style={styles.contactLink}>
              {MAINTENANCE_CONFIG.contactEmail}
            </a>
          </div>
        )}

        {/* Réseaux sociaux */}
        {(MAINTENANCE_CONFIG.instagram || MAINTENANCE_CONFIG.facebook) && (
          <div style={styles.social}>
            {MAINTENANCE_CONFIG.instagram && (
              <a href={MAINTENANCE_CONFIG.instagram} target="_blank" rel="noreferrer" style={styles.socialLink} aria-label="Instagram">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                  <circle cx="12" cy="12" r="4"/>
                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
                </svg>
                <span>Instagram</span>
              </a>
            )}
            {MAINTENANCE_CONFIG.facebook && (
              <a href={MAINTENANCE_CONFIG.facebook} target="_blank" rel="noreferrer" style={styles.socialLink} aria-label="Facebook">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                </svg>
                <span>Facebook</span>
              </a>
            )}
          </div>
        )}
      </div>

      {/* Animation CSS */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600&family=Jost:wght@300;400&display=swap');

        @keyframes floatUp {
          0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 0.6; }
          100% { transform: translateY(-120vh) rotate(360deg); opacity: 0; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseGold {
          0%, 100% { box-shadow: 0 0 0 0 hsla(33,73%,51%,0.3); }
          50% { box-shadow: 0 0 0 12px hsla(33,73%,51%,0); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  root: {
    minHeight: '100vh',
    background: 'hsl(0,0%,9%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Jost', sans-serif",
  },
  bgOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(ellipse 80% 60% at 50% 50%, hsla(33,73%,20%,0.25) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  bgGrain: {
    position: 'absolute',
    inset: 0,
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`,
    backgroundSize: '180px',
    opacity: 0.6,
    pointerEvents: 'none',
  },
  particle: {
    position: 'absolute',
    bottom: '-20px',
    borderRadius: '50%',
    background: 'hsl(33,73%,51%)',
    pointerEvents: 'none',
  },
  container: {
    position: 'relative',
    zIndex: 10,
    textAlign: 'center',
    padding: '3rem 2rem',
    maxWidth: '560px',
    width: '100%',
    animation: 'fadeIn 1.2s ease both',
  },
  logoWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1.5rem',
  },
  logoIcon: {
    padding: '1rem',
    borderRadius: '50%',
    background: 'hsla(33,73%,51%,0.08)',
    border: '1px solid hsla(33,73%,51%,0.2)',
    animation: 'pulseGold 3s ease-in-out infinite',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  siteName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontWeight: 300,
    fontSize: '0.85rem',
    letterSpacing: '0.25em',
    textTransform: 'uppercase',
    color: 'hsl(33,73%,51%)',
    marginBottom: '0.5rem',
  },
  title: {
    fontFamily: "'Cormorant Garamond', serif",
    fontWeight: 600,
    fontSize: 'clamp(2rem, 6vw, 3rem)',
    color: 'hsl(36,33%,97%)',
    margin: '0 0 1.5rem',
    lineHeight: 1.1,
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    marginBottom: '1.75rem',
  },
  dividerLine: {
    display: 'block',
    width: '60px',
    height: '1px',
    background: 'hsla(33,73%,51%,0.4)',
  },
  dividerDot: {
    display: 'block',
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    background: 'hsl(33,73%,51%)',
  },
  message: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.3rem',
    fontWeight: 300,
    fontStyle: 'italic',
    color: 'hsl(36,33%,90%)',
    marginBottom: '0.75rem',
    lineHeight: 1.5,
  },
  subMessage: {
    fontSize: '0.9rem',
    fontWeight: 300,
    color: 'hsla(36,33%,80%,0.7)',
    marginBottom: '2.5rem',
    lineHeight: 1.7,
    letterSpacing: '0.02em',
  },
  countdown: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: '0.25rem',
    marginBottom: '2.5rem',
    position: 'relative',
  },
  countBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
  },
  countNum: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 'clamp(2rem, 8vw, 3.5rem)',
    fontWeight: 300,
    color: 'hsl(33,73%,51%)',
    background: 'hsla(33,73%,51%,0.06)',
    border: '1px solid hsla(33,73%,51%,0.15)',
    borderRadius: '8px',
    padding: '0.5rem 1.2rem',
    minWidth: '72px',
    lineHeight: 1,
    letterSpacing: '0.05em',
  },
  countLabel: {
    fontSize: '0.6rem',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: 'hsla(36,33%,80%,0.5)',
    marginTop: '0.5rem',
    fontWeight: 400,
  },
  countSep: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '2.5rem',
    color: 'hsla(33,73%,51%,0.4)',
    lineHeight: 1,
    paddingBottom: '1.6rem',
    alignSelf: 'flex-end',
    marginBottom: '1.6rem',
  },
  contactWrap: {
    marginBottom: '2rem',
  },
  contactText: {
    fontSize: '0.8rem',
    color: 'hsla(36,33%,80%,0.5)',
    marginBottom: '0.35rem',
    letterSpacing: '0.05em',
  },
  contactLink: {
    color: 'hsl(33,73%,51%)',
    fontSize: '0.9rem',
    textDecoration: 'none',
    borderBottom: '1px solid hsla(33,73%,51%,0.3)',
    paddingBottom: '1px',
    transition: 'border-color 0.2s',
    letterSpacing: '0.02em',
  },
  social: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1.25rem',
  },
  socialLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    color: 'hsla(36,33%,80%,0.55)',
    fontSize: '0.8rem',
    textDecoration: 'none',
    letterSpacing: '0.05em',
    transition: 'color 0.2s',
  },
};

export default Maintenance;
